import { useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useRenderValue } from '@hooks/useRenderValue'
import { getGlobalParams } from '@core/graphQL/globalParams'
import { emitCashOut } from '@sockets/socketEmitters'
import { resolveAutoCashoutInput, clampAutoCashoutValue } from '@core/betting/stakeEngine'
import { useCountdown } from '@hooks/useCountdown'
import {
  availableForPanel,
  deriveFreebetControls,
  isInFlight,
} from '@core/betting/freebetInteractionControls'
import { placeFreebetBet, cancelPendingFreebet } from '@core/betting/freebetBettingOrchestrator'
import { BET_STATE, TOGGLE, ACTION_VARIANT, AUTO_CASHOUT } from '@constants/bettingConstants'
import {
  selectFreebetPanel,
  selectFreebetPanels,
  selectFreebetList,
  selectFreebet,
  markFreebetPending,
  setFreebetAutoCashoutStatus,
  setFreebetAutoCashoutValue,
} from '@slices/bettingSlice'

const ACTION_LABEL = {
  [ACTION_VARIANT.BET]: 'Freebet',
  [ACTION_VARIANT.CANCEL]: 'Cancel',
  [ACTION_VARIANT.PLACED]: 'Bet Placed',
  [ACTION_VARIANT.CASHOUT]: 'Cashout',
}

/**
 * The one hook the freebet cards consume. Same contract as useClassicBetting:
 * all logic lives here + the slice + the pure logic layer; the card only renders
 * what this returns. Pass the FE panel id (PANELS.ONE | PANELS.TWO).
 */
export const useFreebetBetting = (panel) => {
  const dispatch = useDispatch()
  const renderValue = useRenderValue()

  const p = useSelector(selectFreebetPanel(panel))
  const panels = useSelector(selectFreebetPanels)
  const list = useSelector(selectFreebetList)
  const round = useSelector((state) => state.events.round)
  const currencyCode = useSelector((state) => state.wallet.displayCurrencyCode)
  const dualMode = useSelector((state) => Boolean(state.layout?.duelBetMode?.freebets))

  // The freebet this panel shows: the frozen SNAPSHOT while a bet is in flight
  // (so a list refetch mid-round never swaps the card), otherwise the live
  // SELECTION from the list — including after settle/crash, where it re-points to
  // a fresh freebet so the player can bet again for the next round.
  const freebet = useMemo(() => {
    if (isInFlight(p.betState)) return p.placedFreebet
    return list.find((fb) => fb.id === p.selectedFreebetId) ?? null
  }, [p.betState, p.placedFreebet, p.selectedFreebetId, list])

  const hasFreebet = !!freebet

  // The freebets this panel can cycle to (excludes the other panel's freebet).
  const available = useMemo(
    () => availableForPanel({ list, panels, panel, dualMode }),
    [list, panels, panel, dualMode]
  )
  const canSwap = available.length > 1

  // Live expiry — when it hits zero the freebet is time-expired (ROOT may not have
  // cleaned it up yet). Folded into the controls so placing + auto-cashout are
  // blocked here, not in the component.
  const { label: expiryLabel, isExpired } = useCountdown(freebet?.rawExpiringAt)

  const controls = useMemo(
    () => deriveFreebetControls({ panel: p, round, hasFreebet, canSwap, isExpired }),
    [p, round, hasFreebet, canSwap, isExpired]
  )

  const actionButton = useMemo(
    () => ({
      variant: controls.actionVariant,
      label: ACTION_LABEL[controls.actionVariant],
      enabled: controls.actionButton === TOGGLE.ENABLED,
    }),
    [controls.actionVariant, controls.actionButton]
  )

  // Live climbing value while running; settled payout once cashed; else null.
  // DERIVED from the frozen snapshot's amount — never stored, never per-tick.
  const liveCashout = useMemo(() => {
    if (p.betState === BET_STATE.BET_RUNNING) {
      const m = Number(round.multiplier)
      const amt = Number(p.placedFreebet?.rawAmount) || 0
      return m && amt ? amt * m : null
    }
    if (p.betState === BET_STATE.BET_CASHED_OUT) return p.cashoutAmount
    return null
  }, [p.betState, p.placedFreebet, p.cashoutAmount, round.multiplier])

  // ── arrow swap (cyclic over the panel's available list) ─────────────────────
  const swap = useCallback(
    (dir) => {
      if (!available.length) return
      const currentId = freebet?.id ?? null
      const idx = available.findIndex((fb) => fb.id === currentId)
      const base = idx === -1 ? 0 : idx
      const nextIdx = (base + dir + available.length) % available.length
      const nextId = available[nextIdx]?.id
      if (nextId != null) dispatch(selectFreebet({ panel, id: nextId }))
    },
    [available, freebet, dispatch, panel]
  )
  const prev = useCallback(() => swap(-1), [swap])
  const next = useCallback(() => swap(1), [swap])

  // ── auto-cashout (reuses the Classic stake-engine resolvers) ────────────────
  const toggleAutoCashout = useCallback(() => {
    const nextStatus = p.autoCashout.status === TOGGLE.ENABLED ? TOGGLE.DISABLED : TOGGLE.ENABLED
    dispatch(setFreebetAutoCashoutStatus({ panel, status: nextStatus }))
  }, [dispatch, panel, p.autoCashout.status])

  const onAutoCashoutInput = useCallback(
    (raw) => {
      dispatch(
        setFreebetAutoCashoutValue({
          panel,
          value: resolveAutoCashoutInput(raw, {
            max: AUTO_CASHOUT.MAX,
            maxDecimals: AUTO_CASHOUT.DECIMALS,
          }),
        })
      )
    },
    [dispatch, panel]
  )

  const onAutoCashoutBlur = useCallback(() => {
    const raw = p.autoCashout.value
    if (raw == null || raw === '' || raw === '.') return
    const clamped = clampAutoCashoutValue(raw, {
      min: AUTO_CASHOUT.MIN,
      max: AUTO_CASHOUT.MAX,
      maxDecimals: AUTO_CASHOUT.DECIMALS,
    })
    if (clamped !== raw) dispatch(setFreebetAutoCashoutValue({ panel, value: clamped }))
  }, [dispatch, panel, p.autoCashout.value])

  // ── the smart action button ─────────────────────────────────────────────────
  // Pressing Freebet only queues the bet; the submission scheduler sends it just
  // before the round takes off (or immediately, if the press already lands inside
  // that window). Both actions go through the orchestrator, which re-checks live
  // state — `p` here is a render snapshot and can be a frame behind.
  const onActionButton = useCallback(() => {
    if (!actionButton.enabled) return
    switch (actionButton.variant) {
      case ACTION_VARIANT.BET: {
        placeFreebetBet(panel, freebet)
        break
      }
      case ACTION_VARIANT.CANCEL: {
        // Discards a PENDING bet only — nothing was sent, so this is a local clear
        // that also frees the freebet for re-selection. No-op once submitted.
        cancelPendingFreebet(panel)
        break
      }
      case ACTION_VARIANT.CASHOUT: {
        if (p.transactionId) {
          const { gameCode } = getGlobalParams()
          dispatch(markFreebetPending({ panel }))
          emitCashOut({ transactionId: p.transactionId, gameCode })
        }
        break
      }
      default:
        break
    }
  }, [actionButton, freebet, dispatch, panel, p.transactionId])

  return {
    // state
    freebet, // the rendered freebet view-model, or null → "No Freebets available"
    hasFreebet,
    betState: p.betState,
    pending: p.pending,
    autoCashout: p.autoCashout,
    cashoutAmount: p.cashoutAmount,
    transactionId: p.transactionId,
    // derived
    interactionControls: controls,
    liveCashout,
    actionButton,
    canSwap,
    expiry: { label: expiryLabel, isExpired }, // { label, isExpired } — card just renders it
    // formatting
    renderValue,
    currencyCode,
    // actions
    prev,
    next,
    toggleAutoCashout,
    onAutoCashoutInput,
    onAutoCashoutBlur,
    onActionButton,
  }
}
